import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const LegalPage = ({ title, path, updated, children }) => (
  <div data-testid={`legal-${path.slice(1)}`} className="grain-texture min-h-screen">
    <SEO title={title} description={`${title} - Andaman Avalon`} path={path} />
    <Navbar />
    <section className="pt-32 pb-8 bg-primary text-primary-foreground">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl font-serif text-white">{title}</h1>
        <p className="text-white/70 mt-2 text-sm">Last updated: {updated}</p>
      </div>
    </section>
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 legal-content">
        {children}
      </div>
    </section>
    <Footer />
  </div>
);

export const CancellationPolicy = () => (
  <LegalPage title="Cancellation Policy" path="/cancellation-policy" updated="1 February 2026">
    <p>We understand that plans can change. Our cancellation policy is designed to be fair to both our guests and our travel partners (hotels, ferries, activities) who often require non-refundable pre-payments on our end.</p>
    <h2>Refund Slabs</h2>
    <p>Refunds are calculated on the <strong>total package cost paid</strong> and depend on how many days before your travel date you cancel:</p>
    <table>
      <thead><tr><th>Days before travel</th><th>Refund</th></tr></thead>
      <tbody>
        <tr><td>30 days or more</td><td><strong>90% refund</strong> (10% admin fee)</td></tr>
        <tr><td>15–29 days</td><td><strong>50% refund</strong></td></tr>
        <tr><td>7–14 days</td><td><strong>25% refund</strong></td></tr>
        <tr><td>Less than 7 days</td><td><strong>No refund</strong></td></tr>
        <tr><td>No-show</td><td>No refund</td></tr>
      </tbody>
    </table>
    <h2>How to Cancel</h2>
    <p>Email us at <a href="mailto:info@andamanavalon.com">info@andamanavalon.com</a> or WhatsApp <a href="tel:+919933237121">+91 99332 37121</a> with your booking ID. The date we receive your written cancellation request is the cancellation date.</p>
    <h2>Refund Processing</h2>
    <p>Approved refunds are processed within <strong>7–10 working days</strong> back to the original payment method (Razorpay or Stripe). Bank/gateway fees may reduce the credited amount slightly.</p>
    <h2>Force Majeure</h2>
    <p>In case of natural disasters, government-imposed travel restrictions, ferry/flight cancellations, or medical emergencies (with valid documentation), we will do our best to reschedule your trip at no extra cost. Full refunds under such circumstances are at our discretion after deducting non-recoverable third-party costs.</p>
    <h2>Non-Refundable Components</h2>
    <p>Some components are strictly non-refundable regardless of cancellation window:</p>
    <ul>
      <li>Flight tickets (governed by airline policies)</li>
      <li>Non-refundable hotel bookings (pre-paid special-rate rooms)</li>
      <li>Government / port fees already paid</li>
      <li>Visa fees (for foreign nationals)</li>
    </ul>
    <p>These will be deducted from your refund before applying the percentage above.</p>
  </LegalPage>
);

export const TermsAndConditions = () => (
  <LegalPage title="Terms & Conditions" path="/terms" updated="1 February 2026">
    <p>By booking a tour with Andaman Avalon ("we", "us", "our"), you ("guest", "you") agree to these terms.</p>
    <h2>1. Booking & Payment</h2>
    <p>All bookings are confirmed only after receipt of the full package amount or as specified in your itinerary. Prices are in Indian Rupees (INR) and subject to change until payment is completed. Once paid, prices are locked.</p>
    <h2>2. Documents</h2>
    <p>Indian nationals must carry a valid government-issued photo ID (Aadhaar, Passport, Voter ID, Driving Licence). Foreign nationals require a valid passport and RAP (Restricted Area Permit) — we assist with the RAP but you are responsible for holding a valid passport.</p>
    <h2>3. Itinerary Changes</h2>
    <p>Ferry schedules, weather, and government restrictions may force itinerary changes. We reserve the right to modify the itinerary while maintaining the overall experience and value. No refunds are due for weather-related changes.</p>
    <h2>4. Conduct & Liability</h2>
    <p>Guests are responsible for their own safety during activities (scuba, snorkelling, water sports). We partner with certified operators but are not liable for injuries arising from adventure activities, personal negligence, or violation of local laws.</p>
    <h2>5. Damages</h2>
    <p>Any damage caused to hotel property, vehicles, or third-party equipment during your trip is the guest's financial responsibility.</p>
    <h2>6. Complaint Resolution</h2>
    <p>Report any issues in writing within 7 days of trip completion to <a href="mailto:info@andamanavalon.com">info@andamanavalon.com</a>. We commit to a written response within 15 working days.</p>
    <h2>7. Jurisdiction</h2>
    <p>All disputes are subject to the jurisdiction of courts in Port Blair, Andaman & Nicobar Islands, India.</p>
  </LegalPage>
);

export const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" path="/privacy" updated="1 February 2026">
    <p>Your privacy is important to us. This policy explains what data we collect, why, and how we protect it.</p>
    <h2>Data We Collect</h2>
    <ul>
      <li><strong>Booking details:</strong> name, email, phone, travel dates, guest count, package selected, government ID (only for RAP/hotel check-in)</li>
      <li><strong>Payment details:</strong> handled entirely by Razorpay / Stripe — we never see or store your card number, CVV, or UPI PIN</li>
      <li><strong>Website analytics:</strong> anonymous browser + device data via Google Analytics to improve the site experience</li>
    </ul>
    <h2>How We Use It</h2>
    <ul>
      <li>To confirm and deliver your booked tour</li>
      <li>To send booking confirmations, itinerary updates, and important travel notices</li>
      <li>To respond to enquiries and support requests</li>
      <li>To comply with legal obligations (GST invoices, government reporting)</li>
    </ul>
    <h2>What We Never Do</h2>
    <ul>
      <li>We <strong>never sell</strong> your data to third parties for marketing purposes</li>
      <li>We <strong>never share</strong> your government ID beyond mandatory hotel/RAP requirements</li>
      <li>We <strong>never send</strong> promotional emails without your explicit sign-up</li>
    </ul>
    <h2>Data Retention</h2>
    <p>Booking data is retained for 3 years for tax/legal compliance. Enquiry data is retained for 12 months. You can request deletion at any time by emailing <a href="mailto:info@andamanavalon.com">info@andamanavalon.com</a>.</p>
    <h2>Cookies</h2>
    <p>We use minimal cookies for authentication (login sessions) and analytics. No advertising cookies. You can disable cookies in your browser without breaking core site functionality.</p>
    <h2>Your Rights</h2>
    <p>You have the right to access, correct, or delete your personal data. Contact us at <a href="mailto:info@andamanavalon.com">info@andamanavalon.com</a>.</p>
  </LegalPage>
);

export const RefundPolicy = () => (
  <LegalPage title="Refund Policy" path="/refund-policy" updated="1 February 2026">
    <p>This policy explains when and how refunds are processed. For cancellation windows and refund percentages, please see our <a href="/cancellation-policy">Cancellation Policy</a>.</p>
    <h2>Refund Timeline</h2>
    <p>Approved refunds are processed within <strong>7–10 working days</strong> from the date of approval. Refunds are always credited back to the original payment method (never as cash or to a different account).</p>
    <h2>Refund Methods</h2>
    <ul>
      <li><strong>Razorpay payments</strong> (Cards, UPI, Net Banking, Wallets): refunded to the same source. UPI/wallet refunds usually reflect within 24–48 hours. Card/net-banking refunds take 5–7 working days.</li>
      <li><strong>Stripe payments</strong> (International cards): refunded to the same card. Typically visible within 5–10 working days depending on your card issuer.</li>
    </ul>
    <h2>Deductions</h2>
    <p>The following are deducted from any refund before crediting:</p>
    <ul>
      <li>Payment gateway fees (typically 2–3%)</li>
      <li>Non-refundable third-party costs (airline tickets, non-refundable hotel bookings, port fees)</li>
      <li>Bank foreign-exchange spread for international refunds</li>
    </ul>
    <h2>Failed Payments / Double Charges</h2>
    <p>If your card was charged but no booking confirmation was generated, or if you were charged twice for the same booking, we will refund the extra amount within <strong>5 working days</strong> of you reporting it.</p>
    <h2>Chargebacks</h2>
    <p>Please raise a refund request with us before initiating a chargeback with your bank. Unnecessary chargebacks may result in your account being flagged and future bookings declined.</p>
    <h2>Contact</h2>
    <p>For refund-related queries: <a href="mailto:info@andamanavalon.com">info@andamanavalon.com</a> · WhatsApp: <a href="tel:+919933237121">+91 99332 37121</a></p>
  </LegalPage>
);
